import { useMemo, useRef } from 'react';

import Toaster from '@geneui/components/Toaster';

import { useApp } from 'utils/hooks/useApp';
import { useTranslator } from 'utils';

const useFilteredItems = (items, settingKey, keyProperty = 'dataKey', definedFilter) => {
  const { t } = useTranslator();
  const { customizeFieldData } = useApp();
  const warningShownRef = useRef(false);

  return useMemo(() => {
    const excludedKeys =
      settingKey && customizeFieldData?.[settingKey] ? new Set(customizeFieldData[settingKey]) : new Set();

    if (Array.isArray(items)) {
      return items.filter(item => !excludedKeys.has(item[keyProperty]));
    }

    if (typeof items === 'object' && items !== null) {
      const itemKeys = Object.keys(items);
      const definedFilterKeys = definedFilter?.map(item => item.apiKey);

      const result = itemKeys.reduce((acc, key) => {
        const isInDefinedFilter = definedFilter ? definedFilterKeys.includes(key) : true;

        if (!excludedKeys.has(key) && isInDefinedFilter) {
          acc[key] = items[key];
        }
        return acc;
      }, {});

      if (definedFilter && !warningShownRef.current) {
        const resultKeys = Object.keys(result);
        const missingInItems = itemKeys.filter(key => !definedFilterKeys.includes(key));
        const missingInResult = itemKeys.filter(key => !resultKeys.includes(key));

        if (missingInItems.length > 0 || missingInResult.length > 0) {
          Toaster.warning({
            title: 'warning',
            message: t('some-of-the-columns-saved-in-this-filter-are-no-longer-available-and-were-not-applied'),
            iconProps: {
              isFilled: true,
            },
          });
          warningShownRef.current = true;
        }
      }

      return result;
    }

    return items;
  }, [items, settingKey, keyProperty, customizeFieldData, definedFilter]);
};

export default useFilteredItems;
